import React, { ElementType } from 'react';
import {
  Avatar,
  Box,
  IconButton,
  Icons,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import useIsOverflowing from '@app/hooks/useIsOverflowing';
import { createStudentRowStyles } from './StudentRow.styles';
import { StudentRowProps } from './StudentRow.types';

const StudentRow = ({
  canAdd = false,
  isDimmed = false,
  isSelected,
  onToggle,
  onActionClick,
  showAction = true,
  student,
  isDisabled = false,
  disabledText,
  disabledAriaLabel,
  actionIcon,
  actionTooltip,
  component = 'li',
  showAddedLabel = true,
  sx,
}: StudentRowProps) => {
  const theme = useTheme();
  const styles = createStudentRowStyles(theme);
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [nameRef, isNameOverflowing] = useIsOverflowing<HTMLDivElement>();
  const [emailRef, isEmailOverflowing] = useIsOverflowing<HTMLDivElement>();
  const { fullName, lastName } = student;
  const shouldDim = isDimmed || isDisabled;

  if (!fullName) {
    return null;
  }

  const fallbackText =
    fullName
      .slice(0, 1)
      .toUpperCase()
      .concat(
        lastName?.slice(0, 1).toUpperCase() ??
          fullName.split(' ')[1]?.slice(0, 1).toUpperCase() ??
          ''
      ) ?? 'AA';

  const isAddAction = canAdd && !isSelected;
  const CustomActionIcon = actionIcon as ElementType | null | undefined;
  const hasCustomActionIcon = actionIcon !== undefined;
  const shouldShowAction =
    showAction && !isDisabled && (!hasCustomActionIcon || CustomActionIcon);

  const resolvedTooltip =
    actionTooltip ??
    (isAddAction ? 'Add to this extension' : 'Remove from this extension');

  const handleActionClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (onActionClick) {
      event.stopPropagation();
      onActionClick();
      return;
    }
    onToggle?.(student.id);
  };

  const isGroup = component !== 'li';
  const disabledReason =
    disabledAriaLabel ??
    (typeof disabledText === 'string' ? disabledText : undefined);

  const content = (
    <Box
      component={component}
      role={isGroup ? 'group' : undefined}
      tabIndex={0}
      aria-label={[
        student.fullName,
        student.email,
        isDisabled ? disabledReason : undefined,
      ]
        .filter(Boolean)
        .join(', ')}
      aria-disabled={isDisabled || undefined}
      sx={{ ...styles.studentRow, ...sx }}
    >
      <Avatar
        aria-hidden={isGroup || undefined}
        src={student.avatar}
        sx={{
          ...styles.avatar,
          ...(shouldDim && styles.addedStudentAvatar),
        }}
        fallbackText={fallbackText}
      />
      <Box aria-hidden={isGroup || undefined} sx={styles.studentDetails}>
        <Box sx={styles.studentName}>
          <Tooltip
            title={`${student.fullName || ''}${
              isSelected && showAddedLabel ? ' - Added' : ''
            }`}
            disableFocusListener={!isMdUp || !isNameOverflowing || isDisabled}
            disableHoverListener={!isMdUp || !isNameOverflowing || isDisabled}
            disableTouchListener={!isMdUp || !isNameOverflowing || isDisabled}
          >
            <Box
              ref={nameRef}
              sx={{
                ...styles.studentNameText,
                ...(shouldDim && styles.addedStudentText),
              }}
            >
              {student.fullName}
            </Box>
          </Tooltip>
          {isSelected && showAddedLabel && (
            <Box
              display="flex"
              alignItems="center"
              flexShrink={0}
              sx={{ whiteSpace: 'nowrap' }}
            >
              <Box
                component="span"
                sx={{ color: shouldDim ? 'text.secondary' : 'text.primary' }}
              >
                &nbsp;-&nbsp;
              </Box>
              <Box component="span" sx={styles.added}>
                Added
              </Box>
            </Box>
          )}
        </Box>
        <Tooltip
          title={student.email || ''}
          disableFocusListener={!isMdUp || !isEmailOverflowing || isDisabled}
          disableHoverListener={!isMdUp || !isEmailOverflowing || isDisabled}
          disableTouchListener={!isMdUp || !isEmailOverflowing || isDisabled}
        >
          <Box ref={emailRef} sx={styles.studentEmail}>
            {student.email}
          </Box>
        </Tooltip>
      </Box>
      {shouldShowAction && (
        <IconButton
          type="button"
          aria-label={`${resolvedTooltip}, ${student.fullName}`}
          onClick={handleActionClick}
          sx={isAddAction ? styles.addStudent : styles.removeStudent}
        >
          <Tooltip
            disableFocusListener={!isMdUp}
            disableHoverListener={!isMdUp}
            disableTouchListener={!isMdUp}
            title={resolvedTooltip}
          >
            {hasCustomActionIcon && CustomActionIcon ? (
              <CustomActionIcon sx={styles.actionIcon} />
            ) : isAddAction ? (
              <Icons.AddCircle />
            ) : (
              <Icons.RemoveCircle />
            )}
          </Tooltip>
        </IconButton>
      )}
    </Box>
  );

  if (isDisabled && disabledText) {
    return (
      <Tooltip
        title={disabledText}
        disableFocusListener={!isMdUp}
        disableHoverListener={!isMdUp}
        disableTouchListener={!isMdUp}
        position="top"
      >
        <Box sx={styles.disabledWrapper}>{content}</Box>
      </Tooltip>
    );
  }

  return content;
};

export default StudentRow;
